import { format, isSameYear } from 'date-fns'
import { type ClassValue, clsx } from 'clsx'
import { extendTailwindMerge } from 'tailwind-merge'

// Custom @theme tokens from theme.css; unregistered ones get misclassified
// (e.g. `text-meta` as a colour) and dropped when merged.
const twMerge = extendTailwindMerge({
  extend: {
    theme: {
      text: ['meta'],
      spacing: ['control', 'control-sm', 'control-md', 'control-x'],
      shadow: ['panel', 'float'],
      ease: ['liquid'],
      font: ['display', 'heading', 'instrument'],
    },
  },
})

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * A calendar day for billing copy: "1 Oct" this year, "1 Oct 2027" any other
 * year, so a renewal a year away never reads as today.
 */
export function formatDay(
  date: Date | string | number,
  now: Date = new Date()
): string {
  const day = new Date(date)
  return format(day, isSameYear(day, now) ? 'd MMM' : 'd MMM yyyy')
}

export function sleep(ms: number = 1000) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Capitalizes the first letter of a string.
 * @param str - The string to capitalize
 * @returns The string with its first letter capitalized
 */
export function capitalizeFirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

/**
 * Generates page numbers for pagination with ellipsis
 * @param currentPage - Current page number (1-based)
 * @param totalPages - Total number of pages
 * @returns Array of page numbers and ellipsis strings
 *
 * Examples:
 * - Small dataset (≤5 pages): [1, 2, 3, 4, 5]
 * - Near beginning: [1, 2, 3, 4, '...', 10]
 * - In middle: [1, '...', 4, 5, 6, '...', 10]
 * - Near end: [1, '...', 7, 8, 9, 10]
 */
export function getPageNumbers(currentPage: number, totalPages: number) {
  const maxVisiblePages = 5 // Maximum number of page buttons to show
  const rangeWithDots = []

  if (totalPages <= maxVisiblePages) {
    // If total pages is 5 or less, show all pages
    for (let i = 1; i <= totalPages; i++) {
      rangeWithDots.push(i)
    }
  } else {
    // Always show first page
    rangeWithDots.push(1)

    if (currentPage <= 3) {
      // Near the beginning: [1] [2] [3] [4] ... [10]
      for (let i = 2; i <= 4; i++) {
        rangeWithDots.push(i)
      }
      rangeWithDots.push('...', totalPages)
    } else if (currentPage >= totalPages - 2) {
      // Near the end: [1] ... [7] [8] [9] [10]
      rangeWithDots.push('...')
      for (let i = totalPages - 3; i <= totalPages; i++) {
        rangeWithDots.push(i)
      }
    } else {
      // In the middle: [1] ... [4] [5] [6] ... [10]
      rangeWithDots.push('...')
      for (let i = currentPage - 1; i <= currentPage + 1; i++) {
        rangeWithDots.push(i)
      }
      rangeWithDots.push('...', totalPages)
    }
  }

  return rangeWithDots
}

/**
 * Returns up to two uppercase initials taken from the full name, falling back to the email.
 * Gracefully handles empty, null, or whitespace-only inputs.
 */
export function getUserInitials(
  fullName?: string | null,
  email?: string | null
): string {
  const trimmedFullName = normalizeFullName(fullName)
  if (trimmedFullName) {
    const fromName = trimmedFullName
      .split(/\s+/)
      .filter(Boolean)
      .map((segment) => segment[0]!.toUpperCase())
      .join('')
      .slice(0, 2)

    if (fromName) {
      return fromName
    }
  }

  const trimmedEmail = email?.trim()
  if (trimmedEmail) {
    const fromEmail = trimmedEmail
      .replace(/[^A-Za-z0-9]/g, '')
      .slice(0, 2)
      .toUpperCase()

    if (fromEmail) {
      return fromEmail
    }
  }

  return 'U'
}

/**
 * Normalizes a full name by collapsing repeated whitespace and trimming edges.
 * Returns an empty string when the input is nullish or only whitespace.
 */
export function normalizeFullName(value?: string | null): string {
  if (!value) {
    return ''
  }

  return value.replace(/\s+/g, ' ').trim()
}
